import { mergeClassNames } from '../../../utils/mergeClassNames';
import { useContext } from 'react';
import { ToggleButton, type ToggleButtonProps } from 'react-aria-components';
import { BetaSegmentedControlContext } from './SegmentedControl';
import styles from './SegmentedControlOption.module.scss';

export type BetaSegmentedControlOptionProps = {
  /** The id for this option, used to identify the selection. */
  id: string;
  /** The label content for this option. */
  children: ToggleButtonProps['children'];
  /** Whether this specific option is disabled. */
  isDisabled?: boolean;
  /** Renders the option as a square, sized to fit an icon. Use when the option contains only an icon and no text. */
  iconOnly?: boolean;
  /** An accessible label for the option. Required for icon-only options. */
  'aria-label'?: string;
  /** Additional CSS class name. */
  className?: ToggleButtonProps['className'];
  /** The inline style for the element. */
  style?: ToggleButtonProps['style'];
};

/**
 * `BetaSegmentedControlOption` represents an individual option within a
 * {@link BetaSegmentedControl}. Each option must have a unique `value` prop.
 *
 * Options are focusable via Tab. Press Enter or Space to select.
 *
 * ```tsx
 * <BetaSegmentedControl defaultValue="list" aria-label="View mode">
 *   <BetaSegmentedControlOption value="list">List</BetaSegmentedControlOption>
 *   <BetaSegmentedControlOption value="grid">Grid</BetaSegmentedControlOption>
 * </BetaSegmentedControl>
 * ```
 */
export const BetaSegmentedControlOption = ({
  id: value,
  className,
  iconOnly,
  isDisabled,
  ...props
}: BetaSegmentedControlOptionProps) => {
  const { size } = useContext(BetaSegmentedControlContext);

  return (
    <ToggleButton
      className={(renderProps) =>
        mergeClassNames([styles.option, className], renderProps)
      }
      id={value}
      isDisabled={isDisabled}
      data-size={size}
      data-icon-only={iconOnly ?? undefined}
      data-zds
      {...props}
    />
  );
};
